'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { useId, useState } from 'react';
import { useSiteSettings } from '@/components/providers';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import { CONSENT_BANNER_COPY, CONSENT_CATEGORIES } from './consent.constants';
import type { ServiceConsentState } from './consent.types';
import {
    isCategorySelected,
    isServiceSelected,
    selectCategory,
    selectService,
    servicesInCategory,
} from './consent-services';
import { useConsent } from './ConsentProvider';
import { ConsentCategoryItem } from './ConsentCategoryItem';

const PRIMARY_BTN = 'btn btn-primary';
const OUTLINE_BTN = 'btn btn-white-outline';
const TEXT_BTN =
    'text-sm text-(--text-dim) underline underline-offset-4 transition-colors hover:text-(--text-title) cursor-pointer';

export function CookieBanner() {
    const { isBannerVisible, view, setView, hasDecision, undecidedServices, closeBanner, acceptAll, rejectAll } =
        useConsent();
    const { privacySettings } = useSiteSettings();
    const privacyPageSlug = privacySettings?.privacyPage?.slug;
    const headingId = useId();

    if (!isBannerVisible) return null;

    // Services added after an earlier decision: say so instead of repeating the
    // first-visit copy, and keep the existing selection pre-filled underneath.
    const isUpdate = hasDecision && undecidedServices.length > 0;
    const compactTitle = isUpdate ? CONSENT_BANNER_COPY.updateTitle : CONSENT_BANNER_COPY.title;
    const compactDescription = isUpdate ? CONSENT_BANNER_COPY.updateDescription : CONSENT_BANNER_COPY.description;

    const privacyLink = (
        <>
            {' '}
            {CONSENT_BANNER_COPY.privacyPrefix}{' '}
            {privacyPageSlug ? (
                <Link href={`/m/${privacyPageSlug}`} className="underline underline-offset-2 hover:text-(--text-title)">
                    {CONSENT_BANNER_COPY.privacyLinkText}
                </Link>
            ) : (
                CONSENT_BANNER_COPY.privacyLinkText
            )}
            .
        </>
    );

    return (
        <section
            role="dialog"
            aria-modal="false"
            aria-labelledby={headingId}
            aria-label="Datenschutz-Hinweis"
            // Above the floating CTA (85) and the sticky header (80), below the
            // mega menu (200): the consent choice is the one thing on the page
            // that must not end up underneath another control.
            className="fixed inset-x-0 bottom-0 z-[90]"
        >
            <div className="pointer-events-none p-3 sm:p-4">
                <div
                    // The card follows the aperture mode like the page: its
                    // ground is the consent surface (theme/surfaces/consent.css),
                    // its text and buttons the mode's own tokens.
                    className={cn(
                        'pointer-events-auto relative mx-auto flex flex-col overflow-y-auto max-w-page-sm',
                        'max-h-[calc(100dvh-5rem)] bg-(--consent-bg) text-(--text-secondary)',
                        'border border-(--consent-border) p-5 shadow-(--consent-shadow) sm:p-6',
                    )}
                >
                    {hasDecision && (
                        <button
                            type="button"
                            onClick={closeBanner}
                            aria-label={CONSENT_BANNER_COPY.close}
                            className="absolute end-3 top-3 inline-flex size-8 items-center justify-center text-(--text-tertiary) transition-colors hover:text-(--text-title) cursor-pointer"
                        >
                            <UiIcon name="x-mark" className="size-4" />
                        </button>
                    )}

                    {view === 'compact' ? (
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
                            <div className="space-y-1.5">
                                <h2 id={headingId} className="text-base font-medium text-(--text-title)">
                                    {compactTitle}
                                </h2>
                                <p className="text-sm text-(--text-secondary)">
                                    {compactDescription}
                                    {privacyLink}
                                </p>
                            </div>

                            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:flex-nowrap">
                                <button
                                    type="button"
                                    onClick={() => setView('settings')}
                                    className={cn(TEXT_BTN, 'sm:me-2 my-3 sm:my-0')}
                                >
                                    {CONSENT_BANNER_COPY.configure}
                                </button>
                                <button type="button" onClick={rejectAll} className={OUTLINE_BTN}>
                                    {CONSENT_BANNER_COPY.rejectAll}
                                </button>
                                <button type="button" onClick={acceptAll} className={PRIMARY_BTN}>
                                    {CONSENT_BANNER_COPY.acceptAll}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <ConsentSettingsPanel headingId={headingId} privacyLink={privacyLink} />
                    )}
                </div>
            </div>
        </section>
    );
}

function ConsentSettingsPanel({ headingId, privacyLink }: { headingId: string; privacyLink: ReactNode }) {
    const { serviceConsent, services, save, acceptAll, rejectAll, setView } = useConsent();
    // Initialized once on mount; the panel remounts when the view toggles, so it
    // always picks up the current consent without a synchronizing effect. New
    // services simply have no entry yet and therefore start switched off.
    const [selection, setSelection] = useState<ServiceConsentState>(() => serviceConsent);

    // Only list categories the catalog actually covers. Required categories
    // (necessary cookies) always stay visible as a disclosure.
    const visibleCategories = CONSENT_CATEGORIES.filter(
        (category) => category.required || services.some((service) => service.category === category.key),
    );

    return (
        <div className="flex flex-col gap-4">
            {/* Back to the initial compact banner with the three buttons. */}
            <button
                type="button"
                onClick={() => setView('compact')}
                className="-ml-0.5 inline-flex shrink-0 cursor-pointer items-center gap-1.5 self-start text-sm text-(--text-dim) transition-colors hover:text-(--text-title)"
            >
                <UiIcon name="arrow-left" className="size-4" aria-hidden="true" />
                {CONSENT_BANNER_COPY.back}
            </button>

            {/* Grouping only — the whole card scrolls (overflow-y-auto on the card) once it hits the viewport cap. */}
            <div className="space-y-4">
                <div className="space-y-1.5 pe-8">
                    <h2 id={headingId} className="text-base font-medium text-(--text-title)">
                        {CONSENT_BANNER_COPY.settingsTitle}
                    </h2>
                    <p className="text-sm text-(--text-secondary)">
                        {CONSENT_BANNER_COPY.settingsDescription}
                        {privacyLink}
                    </p>
                </div>

                <ul className="-my-1 divide-y divide-(--border-subtle)">
                    {visibleCategories.map((category) => {
                        const categoryServices = servicesInCategory(services, category.key);

                        return (
                            <ConsentCategoryItem
                                key={category.key}
                                category={category}
                                services={categoryServices}
                                checked={isCategorySelected(selection, category.key, categoryServices)}
                                isServiceChecked={(service) => isServiceSelected(selection, service)}
                                onCategoryChange={(checked) =>
                                    setSelection((prev) => selectCategory(prev, categoryServices, checked))
                                }
                                onServiceChange={(service, checked) =>
                                    setSelection((prev) => selectService(prev, service, checked))
                                }
                            />
                        );
                    })}
                </ul>
            </div>

            <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={rejectAll} className={OUTLINE_BTN}>
                    {CONSENT_BANNER_COPY.rejectAll}
                </button>
                <button type="button" onClick={acceptAll} className={OUTLINE_BTN}>
                    {CONSENT_BANNER_COPY.acceptAll}
                </button>
                <button type="button" onClick={() => save(selection)} className={PRIMARY_BTN}>
                    {CONSENT_BANNER_COPY.save}
                </button>
            </div>
        </div>
    );
}
